import React, { useState, useEffect, ChangeEvent } from "react";
import { Link, useParams, useHistory } from "react-router-dom";
import {
    Row,
    Col,
    InputGroup,
    FormControl,
    Form,
    Button,
    Dropdown,
} from "react-bootstrap";
import dlt from "assets/svg/delete.svg";
import dltWhite from "assets/svg/delete-white.svg";
import { useDeleteLdsuModal } from "components/modals/DeleteLdsuModal";

import {
    deleteOneLDSU,
    downloadFastStorage,
    downloadStorage,
    updateMultipleDevicesConfig,
} from "service/gatewayService";
import DefaultModal, { useModal } from "components/modals/ModalTemplate";
import useCollectSort from "hooks/useCollectSort";
import { gatewayReadingStatusStore } from "hooks/gatewayReading";
import HoverAuthorizeTooltip from "components/authorize/AuthorizeTooltip";
import ContentWrapper from "components/content-wrapper/ContentWrapper";

import { isHttpSuccess } from "utils/functions";
import {
    getAPIError,
    showErrorAlert,
    showInfoAlert,
    showSuccessAlert,
} from "utils/alert";
import {
    DefaultMaxCharacter,
    DefaultMinCharacter,
    FAILED_ACTION_MESSAGE,
    Patterns,
    DELETE_LDSU_SUCCESS_MESSAGE,
} from "constant";
import { walletModalTrigger } from "hooks/wallet";
import { DownloadOptions, ResponseDto } from "types";
import { get, some, uniq } from "lodash";
import TimezoneDropdown from "components/common/TimezoneDropdown";
import { useBasicPlanModal } from "hooks/useBasicPlanModal";
import { BasicPlanModal } from "components/modals/BasicPlanModal";
import { useCheckIfBasicPlan } from "hooks/useCheckIfBasicPlan";
import moment, { Moment } from "moment-timezone";
import { useOrganisation } from "hooks/organization";
import DateTimeRangePicker from "components/common/DateTimeRangePicker";
import { DownloadOption } from "./DownloadOption";
import { Box } from "components/common";
import { CombinedLdsu } from "types/Ldsu";
import { GatewayType } from "constant/gateway";
import { IOTPToolTip } from "components/common/Tooltip";
import { Typography } from "@mui/material";

enum AddNameType {
    Prefix = "name_prefix",
    Postfix = "name_postfix",
}

const NAME_PREFIX_MAX_LENGTH = 32;
const { hasLeadingSpaces, hasTrailingSpaces } = Patterns;

type DownloadModalData = {
    start: Moment;
    end: Moment;
    timezone: string;
    error?: string;
};

type CheckedLdsu = {
    UID: string;
    name?: string;
};

const ReachableStatus = {
    REACHABLE: "Reachable",
    NOT_REACHABLE: "Not Reachable",
};

export type LdsuListProps = {
    UUID: string;
    ldsus: CombinedLdsu[];
    gatewayName: string;
    portNumber: number;
    gatewayType: GatewayType;
    setLdsuLength: (length: number) => void;
};

const LdsuList = ({
    UUID: gatewayId,
    ldsus,
    gatewayName,
    portNumber,
    gatewayType,
    setLdsuLength,
}: LdsuListProps) => {
    const { defaultTimeZone } = useOrganisation();
    const [ldsuList, setLdsuList] = useState<Array<CombinedLdsu>>([]);
    const [initialLdsuList, setInitialLdsuList] = useState<Array<CombinedLdsu>>(
        []
    );
    const { data: ldsuCollect, sortIcon } = useCollectSort(ldsuList);
    const [checkedLdsu, setCheckedLdsu] = useState<CheckedLdsu[]>([]);
    const knownDevices = checkedLdsu.filter((l) => l.name);
    const unknownDevices = checkedLdsu.filter((l) => !l.name);
    const {
        openModal,
        modal: ActionModal,
        setModalShow: setActionModalShow,
        setModalData,
        modalData,
    } = useModal();

    const { openModal: openDeleteLdsuModal } = useDeleteLdsuModal();

    // New API State
    const params: any = useParams();
    const history = useHistory();
    // Modal State
    const [modalType, setModalType] = useState("");
    const [modalShow, setModalShow] = useState(false);
    const [modalContent, setModalContent] = useState("");
    const [modalTitle, setModalTitle] = useState("");
    const [uuidToDelete, setUUIDToDelete] = useState("");

    const gatewayReadingData =
        gatewayReadingStatusStore.getState().gatewayReadingData;
    const { IS_BASIC_PLAN } = useCheckIfBasicPlan();
    const { toggleShowBasicPlanModal, showBasicPlanModal }: any =
        useBasicPlanModal();

    useEffect(() => {
        const transformedLdsus = ldsus.map((item: any) => {
            return {
                ...item,
                status: gatewayReadingData[item.UID]
                    ? ReachableStatus.REACHABLE
                    : ReachableStatus.NOT_REACHABLE,
            };
        });
        setLdsuList(transformedLdsus);
        setInitialLdsuList(transformedLdsus);
        setLdsuLength(transformedLdsus.length);
    }, [ldsus, setLdsuLength, gatewayReadingData]);

    const getLdsuName = (ldsu: any) => {
        return get(ldsu, "name", ldsu.NAME);
    };

    const checkReachableStatus = (i: any) => {
        return (
            <div className="d-flex align-items-center ldsuList-row-status overflow-hidden">
                <i
                    className={`material-icons circle ${
                        i.status === ReachableStatus.REACHABLE
                            ? "online"
                            : "offline"
                    }`}
                >
                    circle
                </i>{" "}
                <IOTPToolTip message={i.status}>
                    <Typography noWrap>{i.status}</Typography>
                </IOTPToolTip>
            </div>
        );
    };

    const handleCheckAllChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setCheckedLdsu(ldsuList.map((i) => ({ UID: i.UID, name: i.name })));
        } else {
            setCheckedLdsu([]);
        }
    };

    const handleCheckChange = (
        e: ChangeEvent<HTMLInputElement>,
        i: CombinedLdsu
    ) => {
        if (e.target.checked) {
            setCheckedLdsu((prev: any) =>
                uniq([
                    ...prev,
                    {
                        UID: i.UID,
                        name: i.name,
                    },
                ])
            );
        } else {
            setCheckedLdsu((prev: any) =>
                prev.filter(({ UID }: { UID: string }) => UID !== i.UID)
            );
        }
    };

    const renderLdsus = () => {
        const lst: any = ldsuCollect.map((i: any) => {
            return (
                <div className="table-row" key={i.UID}>
                    <Row className="no-checkbox ldsuList">
                        <div className="ldsuList-row">
                            <div className="ldsuList-row-start">
                                <Form.Check
                                    type="checkbox"
                                    id={i.UID}
                                    checked={some(checkedLdsu, {
                                        UID: i.UID,
                                        name: i.name,
                                    })}
                                    onChange={(e) => handleCheckChange(e, i)}
                                    custom
                                    label={<span className="mr-sm-2"></span>}
                                    aria-label="single-checkbox"
                                />
                            </div>
                            <div className="ldsuList-row-middle">
                                <div className="ldsuList-row-ldsu-name">
                                    <IOTPToolTip message={getLdsuName(i)}>
                                        <Link
                                            to={{
                                                pathname: `/gateway-details/${params.gatewayId}/LDSBus/${params.busNumber}/${i.UID}`,
                                                state: {
                                                    gatewayName: gatewayName,
                                                    portNumber: portNumber,
                                                    ldsuUUID: i.UID,
                                                    UUID: gatewayId,
                                                    ldsuDetails: i,
                                                },
                                            }}
                                        >
                                            <span
                                                dangerouslySetInnerHTML={{
                                                    __html: getLdsuName(
                                                        i
                                                    )?.replace(/ /g, "&nbsp;"),
                                                }}
                                            />
                                        </Link>
                                    </IOTPToolTip>
                                </div>
                                <div className="ldsuList-row-uuid">
                                    <span className="reading-label ">
                                        UUID:{" "}
                                    </span>
                                    {i.UID}
                                </div>
                                {checkReachableStatus(i)}
                                <div className="ldsuList-row-reporting-rate">
                                    {i.INTVL} {i.INTVL ? `sec(s)` : "-"}
                                </div>
                                <div className="ldsuList-row-termination-status">
                                    {i.CMDS ? i.TERM.toUpperCase() : "-"}
                                </div>
                            </div>
                            <div className="ldsuList-row-end">
                                <div className="ldsuList-row-delete-action">
                                    <HoverAuthorizeTooltip permission="gateway:delete">
                                        <img
                                            src={dlt}
                                            alt="delete"
                                            onClick={() => {
                                                handleClickDelete(
                                                    getLdsuName(i),
                                                    i.UID
                                                );
                                            }}
                                            onKeyDown={() => {
                                                handleClickDelete(
                                                    getLdsuName(i),
                                                    i.UID
                                                );
                                            }}
                                        />
                                    </HoverAuthorizeTooltip>
                                </div>
                                <div className="ldsuList-row-detail-action">
                                    <Link
                                        to={{
                                            pathname: `/gateway-details/${params.gatewayId}/LDSBus/${params.busNumber}/${i.UID}`,
                                            state: {
                                                gatewayName: gatewayName,
                                                portNumber: portNumber,
                                                ldsuUUID: i.UID,
                                                UUID: gatewayId,
                                                ldsuDetails: i,
                                            },
                                        }}
                                    >
                                        <i className="material-icons text-primary-green-1">
                                            keyboard_arrow_right
                                        </i>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </Row>
                </div>
            );
        });

        if (lst.length === 0) {
            lst.push(
                <div className="mt-5 text-center w-100" key="no-ldsu">
                    No LDSUs to display
                </div>
            );
        }
        return lst;
    };

    const searchLdsu = (value: string) => {
        if (value) {
            const filteredLdsu = [];
            for (const i of initialLdsuList) {
                if (
                    getLdsuName(i)?.toLowerCase().includes(value.toLowerCase())
                ) {
                    filteredLdsu.push(i);
                }
            }
            setLdsuList(filteredLdsu);
            setLdsuLength(filteredLdsu.length);
        } else {
            setLdsuList(ldsus);
            setLdsuLength(ldsus.length);
        }
    };

    const handleClickDelete = (ldsu_name: string, ldsu_uuid: string) => {
        setUUIDToDelete(ldsu_uuid);
        setModalType("dlt");
        setModalTitle("Delete LDSU?");
        setModalShow(true);
        setModalContent(`Are you sure you want to delete LDSU ${ldsu_name}?`);
    };

    const deleteThisLdsu = async () => {
        const deleteRes: any = await deleteOneLDSU(
            params.gatewayId,
            uuidToDelete
        );

        if (isHttpSuccess(deleteRes.status)) {
            showSuccessAlert({
                message: DELETE_LDSU_SUCCESS_MESSAGE,
            });

            history.push(
                `/gateway-details/${params.gatewayId}/LDSBus/${params.busNumber}`,
                {
                    refresh: [uuidToDelete],
                }
            );

            setCheckedLdsu(
                checkedLdsu.filter((checked) => checked.UID !== uuidToDelete)
            );
        } else {
            showErrorAlert(getAPIError(deleteRes, FAILED_ACTION_MESSAGE));
        }

        setModalTitle("");
        setModalShow(false);
    };

    const deleteSelectedLdsuHandler = async () => {
        const deleteFunction = async () => {
            const successResList: ResponseDto[] = [];
            const failedResList: ResponseDto[] = [];

            const responses = await Promise.all(
                checkedLdsu.map(({ UID }: { UID: string }) => {
                    return deleteOneLDSU(params.gatewayId, UID);
                })
            );

            responses.forEach((r) => {
                if (r && typeof r === "object" && "status" in r) {
                    isHttpSuccess(r.status)
                        ? successResList.push(r)
                        : failedResList.push(r);
                }
            });

            if (successResList.length > 0) {
                showSuccessAlert({
                    message: `${successResList.length} LDSUs are deleted successfully.`,
                });

                const successResListUIDs = successResList.map(
                    (r) => r.data.device
                );
                history.push(
                    `/gateway-details/${params.gatewayId}/LDSBus/${params.busNumber}`,
                    {
                        refresh: successResListUIDs,
                    }
                );
            }

            failedResList.forEach((frl) => {
                frl.message &&
                    showErrorAlert({
                        message: frl.message,
                    });
            });

            setLdsuList((prev) =>
                prev.filter(
                    (ldsu) =>
                        !successResList.some((r) => r.data.device === ldsu.UID)
                )
            );
            setLdsuLength(ldsuList.length - successResList.length);

            setCheckedLdsu(
                checkedLdsu.filter(
                    (cl) =>
                        !successResList
                            .map((srl) => srl.data.device)
                            .includes(cl.UID)
                )
            );
        };

        openDeleteLdsuModal({
            modelData: {
                isDeleteAll: false,
                itemLength: checkedLdsu.length,
                busNumber:
                    gatewayType !== GatewayType.LoRaWAN
                        ? Number(params.busNumber)
                        : null,
            },
            onConfirm: deleteFunction,
        });
    };

    const lengthExceed = ({
        type,
        prefix,
        postfix,
    }: {
        type: string;
        prefix: string;
        postfix: string;
    }) => {
        const charactersToAdd =
            type === AddNameType.Prefix ? prefix.length : postfix.length;

        return knownDevices.some((l: any) => {
            return l.name.length + charactersToAdd > DefaultMaxCharacter;
        });
    };

    const isInputVerified = (openedModalData: any) => {
        if (
            openedModalData.type === AddNameType.Prefix &&
            hasLeadingSpaces.test(openedModalData.name_prefix)
        ) {
            showErrorAlert({
                message: "Not allowed to have leading spaces for prefix.",
            });
            return false;
        } else if (
            openedModalData.type === AddNameType.Postfix &&
            hasTrailingSpaces.test(openedModalData.name_postfix)
        ) {
            showErrorAlert({
                message: "Not allowed to have trailing spaces for suffix.",
            });
            return false;
        }

        if (
            lengthExceed({
                type: openedModalData.type,
                prefix: openedModalData.name_prefix,
                postfix: openedModalData.name_postfix,
            })
        ) {
            showErrorAlert({
                message: `LDSU Name must be between ${DefaultMinCharacter}-${DefaultMaxCharacter} characters`,
            });
            return false;
        }
        return true;
    };

    const unknownDeviceInfoAlert = () => {
        unknownDevices.length &&
            showInfoAlert({
                message: `Please note that this operation will not be performed on unknown LDSU(s). Unknown LDSU(s): ${unknownDevices.map(
                    (d) => d.UID
                )}`,
            });
    };

    const updatePrefixSuffix = (openedModalData: any) => {
        if (!isInputVerified(openedModalData)) return;

        openModal({
            key: "add_prefix_suffix_confirm",
            modalTitle: "Confirmation",
            modalType: "updateConfirm",
            modalContent:
                "Are you sure you want to update the name of {length} LDSUs?".fill(
                    checkedLdsu
                ),
            resendFunction: async () => {
                unknownDeviceInfoAlert();

                if (knownDevices.length) {
                    const { status, data }: { status: number; data?: any } =
                        (await updateMultipleDevicesConfig({
                            devices: knownDevices.map(
                                ({ UID }: { UID: string }) => ({
                                    gateway_id: gatewayId,
                                    device_id: UID,
                                })
                            ),
                            ...(openedModalData.type === AddNameType.Prefix &&
                                openedModalData.name_prefix && {
                                    name_prefix: openedModalData.name_prefix,
                                }),
                            ...(openedModalData.type === AddNameType.Postfix &&
                                openedModalData.name_postfix && {
                                    name_postfix: openedModalData.name_postfix,
                                }),
                        })) ?? {};

                    if (isHttpSuccess(status)) {
                        showSuccessAlert({
                            message: `Name of ${knownDevices.length} LDSUs are updated successfully.`,
                        });

                        setLdsuList((prev) =>
                            prev.map((lds) => {
                                if (
                                    some(knownDevices, {
                                        UID: lds.UID,
                                        name: lds.name,
                                    })
                                ) {
                                    return {
                                        ...lds,
                                        name:
                                            ((openedModalData.type ===
                                                AddNameType.Prefix &&
                                                openedModalData.name_prefix) ||
                                                "") +
                                            lds.name +
                                            ((openedModalData.type ===
                                                AddNameType.Postfix &&
                                                openedModalData.name_postfix) ||
                                                ""),
                                    };
                                } else {
                                    return lds;
                                }
                            })
                        );
                        setModalData({
                            type: AddNameType.Prefix,
                            name_prefix: "",
                            name_postfix: "",
                        });
                    } else {
                        showErrorAlert({
                            title: data?.title,
                            message: data?.description,
                        });
                    }
                }
                setCheckedLdsu([]);
            },
        });
    };

    const closePrefixSuffixHandlerModal = () => {
        setActionModalShow(false);
        setModalData({
            type: AddNameType.Prefix,
            name_prefix: "",
            name_postfix: "",
        });
    };

    const updatePrefixSuffixHandler = async () => {
        setModalData({
            type: AddNameType.Prefix,
            name_prefix: "",
            name_postfix: "",
        });

        const modalHeaderButton = () => {
            return (
                <Button
                    variant=""
                    className="close-button"
                    onClick={closePrefixSuffixHandlerModal}
                >
                    <span className="material-icons">close</span>
                </Button>
            );
        };

        const modalContent = (openedModalData: any) => {
            return (
                <Form
                    onChange={(event) => {
                        const data = event.currentTarget.toData<{
                            type: AddNameType.Prefix | AddNameType.Postfix;
                            [key: string]: string;
                        }>();

                        setModalData((prev) => ({
                            ...prev,
                            type: data.type,
                            [data.type]: data[data.type],
                        }));
                    }}
                >
                    <Row className="text-gray open-modal">
                        {Object.values(AddNameType).map((type) => (
                            <Col
                                key={type}
                                lg={{
                                    span: 3,
                                }}
                                xs={{
                                    span: 6,
                                }}
                            >
                                <Form.Check
                                    id={`ps_type_${type}`}
                                    type="radio"
                                    label={
                                        type === AddNameType.Prefix
                                            ? "Prefix"
                                            : "Suffix"
                                    }
                                    name="type"
                                    value={type}
                                    checked={openedModalData.type === type}
                                    onChange={() => {}}
                                    custom
                                />
                            </Col>
                        ))}
                    </Row>

                    <Form.Group className="d-flex align-items-center mt-4">
                        <span
                            className={`text-gray ${
                                openedModalData.type === AddNameType.Prefix
                                    ? "order-2 ml-2 "
                                    : "order-1 mr-2"
                            }`}
                        >
                            [Name]
                        </span>
                        {Object.values(AddNameType).map((i) => (
                            <Form.Control
                                key={i}
                                type="text"
                                id={i}
                                className={`${
                                    openedModalData.type !== i ? "d-none" : ""
                                } ${
                                    openedModalData.type === AddNameType.Prefix
                                        ? "order-1"
                                        : "order-2"
                                } ldsu-prefix-suffix-input`}
                                name={i}
                                onChange={(event: any) => {
                                    if (event.target.value.length > 0) {
                                        // Replace all non-ASCII characters

                                        const cleanedInput =
                                            event.target.value.replace(
                                                /[^\x20-\x7F]/g,
                                                ""
                                            );
                                        setModalData({
                                            ...modalData,
                                            type: i,
                                            [event.target.name]: cleanedInput,
                                        });
                                    }
                                }}
                                isInvalid={
                                    openedModalData[i]?.trim().length >
                                    NAME_PREFIX_MAX_LENGTH
                                }
                                aria-label={"input " + i.split("_")[1]}
                            />
                        ))}
                    </Form.Group>
                </Form>
            );
        };

        const modalFooter = (openedModalData: any) => {
            return (
                <div className="w-100 d-flex justify-content-between align-items-center m-0">
                    <span className="text-gray">
                        {"{length} LDSU(s) selected".fill(checkedLdsu)}
                    </span>

                    <Button
                        disabled={
                            (openedModalData.type === AddNameType.Prefix &&
                                !openedModalData.name_prefix) ||
                            (openedModalData.type === AddNameType.Postfix &&
                                !openedModalData.name_postfix) ||
                            (openedModalData.type === AddNameType.Prefix &&
                                openedModalData.name_prefix?.length >
                                    NAME_PREFIX_MAX_LENGTH) ||
                            (openedModalData.type === AddNameType.Postfix &&
                                openedModalData.name_postfix?.length >
                                    NAME_PREFIX_MAX_LENGTH)
                        }
                        onClick={() => {
                            updatePrefixSuffix(openedModalData);
                        }}
                    >
                        UPDATE
                    </Button>
                </div>
            );
        };
        openModal({
            key: "add_prefix_suffix",
            modalType: "content",
            modalTitle: "Add Prefix/Suffix",
            width: 635,
            modalHeaderButton,
            modalContent,
            modalFooter,
        });
    };

    const updateReportRate = (openedModalData: any) => {
        openModal({
            key: "change_report_rate_confirm",
            modalType: "updateConfirm",
            modalTitle: "Confirmation",
            modalContent:
                "Are you sure you want to update the Report Rate of sensors in {length} LDSUs?".fill(
                    checkedLdsu
                ),
            resendFunction: async () => {
                unknownDeviceInfoAlert();

                if (knownDevices.length) {
                    const { status, data }: { status: number; data?: any } =
                        (await updateMultipleDevicesConfig({
                            INTVL: Number(openedModalData.report_rate),
                            devices: knownDevices.map(
                                ({ UID }: { UID: string }) => ({
                                    gateway_id: gatewayId,
                                    device_id: UID,
                                })
                            ),
                        })) ?? {};

                    if (isHttpSuccess(status)) {
                        showSuccessAlert({
                            message: `Report Rate of sensors in ${knownDevices.length} LDSUs are updated successfully.`,
                        });
                    } else {
                        showErrorAlert({
                            title: data?.title,
                            message: data?.description,
                        });
                        return;
                    }
                }

                setLdsuList((prev) =>
                    prev.map((lds) => {
                        if (
                            knownDevices.some(
                                (checked) => checked.UID === lds.UID
                            )
                        ) {
                            return {
                                ...lds,
                                INTVL: Number(openedModalData.report_rate),
                            };
                        }
                        return lds;
                    })
                );

                setCheckedLdsu([]);
            },
        });
    };

    const changeReportRateHandler = () => {
        setModalData({
            report_rate: 5,
        });

        const modalContent = (openedModalData: any) => {
            return (
                <Form>
                    <Form.Group className="text-gray input-right-text">
                        <Form.Label className="text-left w-100">
                            Report Rate
                        </Form.Label>
                        <InputGroup>
                            <Form.Control
                                type="text"
                                name="report_rate"
                                value={openedModalData.report_rate}
                                isInvalid={
                                    !openedModalData.report_rate ||
                                    openedModalData.report_rate % 5 !== 0 ||
                                    openedModalData.report_rate < 5 ||
                                    openedModalData.report_rate > 1800
                                }
                                onChange={(e) => {
                                    setModalData({
                                        report_rate: e.currentTarget.value,
                                    });
                                }}
                                aria-label="input report rate"
                            />
                            <span>sec(s)</span>
                        </InputGroup>
                    </Form.Group>
                    <p className="w-100 text-left fs-14">
                        Minimum - 5 seconds, Maximum - 1800 seconds (30 minutes)
                        and value should be multiple of 5 seconds.
                    </p>
                </Form>
            );
        };

        const modalFooter = (openedModalData: any) => {
            return (
                <div className="w-100 d-flex justify-content-between align-items-center m-0">
                    <span className="text-gray">
                        {"{length} LDSU(s) selected".fill(checkedLdsu)}
                    </span>

                    <Button
                        disabled={
                            !Number(openedModalData.report_rate) ||
                            Number(openedModalData.report_rate) % 5 !== 0 ||
                            Number(openedModalData.report_rate) < 5 ||
                            Number(openedModalData.report_rate) > 1800
                        }
                        onClick={() => {
                            updateReportRate(openedModalData);
                        }}
                    >
                        UPDATE
                    </Button>
                </div>
            );
        };
        openModal({
            key: "change_report_rate",
            modalType: "content",
            modalTitle: "Change Report Rate",
            width: 635,
            modalContent,
            modalFooter,
        });
    };

    const handleDateTimeRangeChange = (value: Moment[]) => {
        const disabledFastDownload =
            value[1].diff(value[0], "minute") > 24 * 60;

        setModalData((prev) => ({
            ...prev,
            start: value[0],
            end: value[1],
            downloadOption: disabledFastDownload
                ? "email"
                : prev.downloadOption,
            isDisableFastDownload: disabledFastDownload,
        }));
    };

    const handleDateTimeRangeError = (error: string) => {
        setModalData((prev) => ({
            ...prev,
            error,
        }));
    };

    const handleChangeTimeZone = (
        timezone: string,
        data: DownloadModalData
    ) => {
        const { start, end, error } = data;
        const startDate = moment.tz(+start, timezone);
        const endDate = moment.tz(+end, timezone);
        setModalData({
            start: startDate,
            end: endDate,
            timezone,
            error,
        });
    };

    const downloadData = (openedModalData: {
        start: Moment;
        end: Moment;
        timezone: string;
        downloadOption: DownloadOptions;
    }) => {
        const { start, end, timezone, downloadOption } = openedModalData;
        openModal({
            key: "download_data_confirm",
            modalType: "updateConfirm",
            modalTitle: "Confirmation",
            modalContent: (
                <>
                    {downloadOption === "email" && (
                        <p
                            dangerouslySetInnerHTML={{
                                __html: `By confirming, an email will be sent to your email address with instructions on how to download.<br />Do you want to continue?`,
                            }}
                        />
                    )}
                    {downloadOption === "fast" && (
                        <p
                            dangerouslySetInnerHTML={{
                                __html: `By confirming, data will be ready for download shortly. Do you want to continue?`,
                            }}
                        />
                    )}
                </>
            ),
            resendFunction: async () => {
                unknownDeviceInfoAlert();

                if (knownDevices.length) {
                    const params = {
                        start: start.seconds(0).unix(),
                        end: end.seconds(0).unix(),
                        attributes: knownDevices.map(
                            ({ UID }: { UID: string }) => ({
                                gateway_id: gatewayId,
                                device_id: UID,
                            })
                        ),
                        time_zone: timezone,
                    };

                    const res =
                        (await (downloadOption === "fast"
                            ? downloadFastStorage(params)
                            : downloadStorage(params))) || {};

                    if (isHttpSuccess(res.status)) {
                        if (downloadOption === "email") {
                            showSuccessAlert({
                                message:
                                    "An email will be sent to you shortly once it is ready to download.",
                            });
                        } else {
                            const fileName = get(
                                res,
                                "headers['content-disposition']"
                            )
                                .split("filename=")[1]
                                .replace(/"/gi, "");
                            const link = document.createElement("a");
                            link.href = global.URL.createObjectURL(res.data);

                            link.download = fileName;
                            link.click();

                            global.URL.revokeObjectURL(link.href);
                            showSuccessAlert({
                                message:
                                    "Data for the selected range will be downloaded.",
                            });
                        }
                    } else {
                        showErrorAlert(
                            getAPIError(
                                res,
                                "Unable to download data. Please try again."
                            )
                        );
                    }
                }

                setCheckedLdsu([]);
            },
        });
    };

    const downloadDataHandler = () => {
        if (IS_BASIC_PLAN) {
            toggleShowBasicPlanModal();
            return;
        }

        const modalContent = (openedModalData: any) => {
            const {
                start,
                end,
                timezone,
                downloadOption,
                isDisableFastDownload,
            } = openedModalData;
            return (
                <Form>
                    <div>
                        <div className="text-gray">Time Zone</div>
                        <Box className="mt-2 light">
                            <Col>
                                <Form.Group className="text-gray input-right-text modal-date-picker">
                                    <Form.Label className="text-left w-100">
                                        Select a time zone
                                    </Form.Label>
                                    <TimezoneDropdown
                                        value={timezone}
                                        onChange={(tz) =>
                                            handleChangeTimeZone(
                                                tz,
                                                openedModalData
                                            )
                                        }
                                    />
                                </Form.Group>
                            </Col>
                        </Box>
                    </div>
                    <div className="mt-3">
                        <div className="text-gray">Date/time Range</div>
                        <Box className="mt-2 light">
                            <Col>
                                <DateTimeRangePicker
                                    inline={true}
                                    value={[start, end]}
                                    timezone={timezone}
                                    onChange={handleDateTimeRangeChange}
                                    onError={handleDateTimeRangeError}
                                />
                            </Col>
                        </Box>
                    </div>
                    <div className="mt-3">
                        <DownloadOption
                            selectedOption={downloadOption}
                            onChange={(option) =>
                                setModalData({
                                    ...openedModalData,
                                    downloadOption: option,
                                })
                            }
                            fastDisabled={isDisableFastDownload}
                        />
                    </div>
                </Form>
            );
        };

        const modalFooter = (openedModalData: any) => {
            return (
                <div className="w-100 d-flex justify-content-between align-items-center m-0">
                    <span className="text-gray">
                        {"{length} LDSU(s) selected".fill(checkedLdsu)}
                    </span>
                    <Button
                        data-testid="ldsuDownloadData"
                        disabled={!!openedModalData.error}
                        onClick={() => {
                            downloadData(openedModalData);
                        }}
                    >
                        DOWNLOAD
                    </Button>
                </div>
            );
        };

        walletModalTrigger(() => {
            setModalData({
                start: moment.utc().tz(defaultTimeZone).subtract(1, "day"),
                end: moment.utc().tz(defaultTimeZone),
                timezone: defaultTimeZone,
                downloadOption: "email",
                isDisableFastDownload: false,
            });

            openModal({
                key: "download_data",
                modalType: "content",
                modalTitle: "Download LDSU Data",
                width: 635,
                modalContent,
                modalFooter,
            });
        });
    };

    return (
        <ContentWrapper>
            <Row className="mt-4">
                <Col className="table-head-options d-flex">
                    <Button
                        variant="danger"
                        className="mini-button mr-2"
                        onClick={deleteSelectedLdsuHandler}
                        disabled={checkedLdsu.length === 0}
                        aria-label="batch delete"
                    >
                        <img src={dltWhite} alt="delete" />
                    </Button>
                    <Button
                        variant="default"
                        className="mini-button btn-mute add-prefix-suffix mr-2"
                        disabled={checkedLdsu.length === 0}
                        onClick={updatePrefixSuffixHandler}
                        aria-label="add prefix/suffix"
                    >
                        <span className="symbol">+</span>
                        {""}
                        Add Prefix/Suffix to Name
                    </Button>
                    <Dropdown className="dash-more">
                        <Dropdown.Toggle
                            variant="default"
                            className="mini-button btn-mute"
                            disabled={checkedLdsu.length === 0}
                            aria-label="more"
                        >
                            <span className="material-icons">more_horiz</span>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item
                                onClick={changeReportRateHandler}
                                className="dropdown-item"
                                aria-label="change report rate"
                                disabled={gatewayType === GatewayType.LoRaWAN}
                            >
                                Change Report Rate
                            </Dropdown.Item>

                            <Dropdown.Item
                                onClick={downloadDataHandler}
                                className="dropdown-item"
                                aria-label="download data"
                            >
                                Download Data
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </Col>
                <Col className="table-head-options">
                    <div className="search float-right">
                        <InputGroup>
                            <FormControl
                                type="text"
                                placeholder="Search.."
                                aria-describedby="button-addon2"
                                aria-label="search ldsu"
                                onChange={(e) => searchLdsu(e.target.value)}
                            />
                        </InputGroup>
                    </div>
                </Col>
            </Row>
            <Row className="cstm-table mt-4">
                <div className="table-head">
                    <Row className="no-checkbox">
                        <Col sm="1" className="ldsuList-col-start">
                            <Form.Check
                                type="checkbox"
                                id="ldsus-select-all"
                                onChange={handleCheckAllChange}
                                checked={
                                    checkedLdsu.length > 0 &&
                                    checkedLdsu.length === ldsuList.length
                                }
                                custom
                                label={<span className="mr-sm-2"></span>}
                                aria-label="select all ldsus"
                            />
                        </Col>
                        <Col className="ldsuList-col-ldsu-name">
                            LDSU Name {sortIcon("name")}
                        </Col>
                        <Col className="ldsuList-col-uuid">
                            UUID {sortIcon("UID")}
                        </Col>
                        <Col className="ldsuList-col-status">
                            Status {sortIcon("status")}
                        </Col>
                        <Col className="ldsuList-col-reporting-rate">
                            Reporting Rate {sortIcon("INTVL")}
                        </Col>
                        <Col className="ldsuList-col-termination-status">
                            Termination Status {sortIcon("TERM")}
                        </Col>
                        <Col sm="1">Action</Col>
                        <Col sm="1"></Col>
                    </Row>
                </div>
                {renderLdsus()}
            </Row>
            <DefaultModal
                modalShow={modalShow}
                setModalShow={setModalShow}
                modalType={modalType}
                modalContent={modalContent}
                modalTitle={modalTitle}
                deleteFunction={deleteThisLdsu}
                okAction={() => {
                    setModalTitle("");
                    history.go(0);
                }}
            />
            {ActionModal}
            <BasicPlanModal
                showBasicPlanModal={showBasicPlanModal}
                toggleShowBasicPlanModal={toggleShowBasicPlanModal}
            />
        </ContentWrapper>
    );
};

export default LdsuList;
